import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, Renderer2, SimpleChanges } from '@angular/core';
import { PaymentsService } from '../../services/payments.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import * as XLSX from 'xlsx-js-style';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * AP remittance importer, embedded in Vendor Payment Entry (which itself is
 * embedded in bank rec's "Post payment / allocate" mode). Drop / paste the
 * vendor's remittance or draft-detail (CSV, Excel, PDF, or pasted rows); the
 * server parses + matches its lines to the vendor's open supptrans invoices;
 * the operator reviews; then ONE click applies the included lines onto the
 * payment screen's allocation checkboxes. Posting stays in the host screen's
 * existing Post Payments flow — this component never writes money.
 *
 * The AP twin of shared/payment/remittance-import.component (AR side).
 */
@Component({
	selector: 'app-ap-remittance-import',
	templateUrl: './ap-remittance-import.component.html',
	styleUrls: ['./ap-remittance-import.component.scss']
})
export class ApRemittanceImportComponent implements OnInit, OnChanges, OnDestroy {
	@Input() vendor: any = null;                 // the open vendor (has supplierid, suppname)
	@Input() bankRecContext: any = null;         // present when hosted inside bank rec — enables the bank-line variance stat
	@Output() apply = new EventEmitter<any>();   // { batch_id, lines: [{ id, supptransid, invoice_ref, alloc }], stated_total, applied_total }
	@Output() openChange = new EventEmitter<boolean>();   // host hides its Allocate section while the importer is open
	@Output() postNow = new EventEmitter<void>();         // "Post Payment" after apply — host stages the form payment and runs its normal postPayments()

	// Emitted on a microtask so an open-during-init (scratchpad restore) can't
	// trip ExpressionChangedAfterChecked on the host's *ngIf.
	private emitOpen(): void {
		Promise.resolve().then(() => this.openChange.emit(this.open));
	}

	open = false;                                 // this component owns its own show/hide
	expanded = false;                             // focus/maximize mode (fullscreen overlay)
	filterStatus: string | null = null;          // click a summary chip to filter the table
	mode: 'input' | 'review' | 'done' = 'input';
	pasteText = '';
	fileName = '';
	fileBase64 = '';
	fileKind: 'pdf' | 'csv' | '' = '';

	parsing = false;
	batch: any = null;
	appliedToPayment = false;                     // lines pushed onto the host screen's allocations
	appliedResult: any = null;                    // set once the host reports the payment posted

	// Netted drafts (invoices MINUS credits = the wire) only reconcile if the credit
	// notes are applied too. On by default when the batch has matched credits: that
	// IS the vendor's instruction, and leaving it off is what forced 160 rows of
	// hand-ticking in the Allocate section.
	applyCredits = true;
	creditPlan: any = null;                       // last plan from the server (read-only, capped)

	constructor(private paymentsService: PaymentsService, private globalSearchService: GlobalSearchService,
		private el: ElementRef, private renderer: Renderer2) {}

	ngOnInit(): void {
		this.restoreScratch(this.vendor);   // survive a page refresh mid-review
	}

	// A page refresh (or vendor switch) destroys the in-memory batch; stash the working review
	// in localStorage keyed to the vendor. Mirrors the AR importer's scratchpad.
	private readonly SCRATCH_KEY = 'ap_remit_scratchpad_v1';

	ngOnChanges(changes: SimpleChanges): void {
		const c = changes['vendor'];
		if (!c) { return; }
		const prev = c.previousValue, cur = c.currentValue;
		if (prev?.supplierid && this.mode === 'review' && this.batch) { this.persistScratch(prev); }
		const switched = !prev || prev.supplierid !== cur?.supplierid;
		if (cur?.supplierid && switched) {
			this.resetInMemory();
			this.restoreScratch(cur);
		}
	}

	@HostListener('window:beforeunload')
	onBeforeUnload(): void { this.persistScratch(); }

	persistScratch(vend?: any): void {
		const v = vend || this.vendor;
		if (!v?.supplierid || this.mode !== 'review' || !this.batch) { return; }
		try {
			localStorage.setItem(this.SCRATCH_KEY, JSON.stringify({
				supplierid: v.supplierid,
				batch: this.batch,
				appliedToPayment: this.appliedToPayment,
				applyCredits: this.applyCredits,
				filterStatus: this.filterStatus, searchText: this.searchText,
				ts: Date.now(),
			}));
		} catch (e) { /* storage unavailable/full — non-fatal */ }
	}

	private restoreScratch(vend: any): void {
		if (!vend?.supplierid) { return; }
		let s: any = null;
		try { s = JSON.parse(localStorage.getItem(this.SCRATCH_KEY) || 'null'); } catch (e) { s = null; }
		if (!s || !s.batch || String(s.supplierid) !== String(vend.supplierid)) { return; }
		this.batch = s.batch;
		this.appliedToPayment = !!s.appliedToPayment;
		this.applyCredits = s.applyCredits !== false;
		this.filterStatus = s.filterStatus ?? null;
		this.searchText = s.searchText || '';
		this.mode = 'review';
		this.open = true;
		this.emitOpen();
	}

	private clearScratch(): void {
		try { localStorage.removeItem(this.SCRATCH_KEY); } catch (e) { /* noop */ }
	}

	// Drop the in-memory review without touching localStorage (used when switching vendors).
	private resetInMemory(): void {
		this.batch = null; this.appliedToPayment = false; this.appliedResult = null;
		this.applyCredits = true; this.creditPlan = null;
		this.filterStatus = null; this.searchText = ''; this.mode = 'input';
	}

	toggle(): void {
		this.open = !this.open;
		if (!this.open && this.expanded) { this.exitExpand(); }
		this.emitOpen();
	}

	// Focus mode. position:fixed is unreliable inside modal/transformed ancestors
	// (bank rec's posting pane), so the host is teleported to <body>.
	private origParent: any = null;
	private origNext: any = null;

	toggleExpand(): void {
		this.expanded ? this.exitExpand() : this.enterExpand();
	}
	private enterExpand(): void {
		if (this.expanded) { return; }
		this.expanded = true;
		this.open = true;
		this.emitOpen();
		const host = this.el.nativeElement;
		this.origParent = host.parentNode;
		this.origNext = host.nextSibling;
		this.renderer.appendChild(document.body, host);
		this.renderer.addClass(document.body, 'remit-focus-lock');
	}
	private exitExpand(): void {
		if (!this.expanded) { return; }
		this.expanded = false;
		const host = this.el.nativeElement;
		if (this.origParent) {
			if (this.origNext) { this.renderer.insertBefore(this.origParent, host, this.origNext); }
			else { this.renderer.appendChild(this.origParent, host); }
		}
		this.renderer.removeClass(document.body, 'remit-focus-lock');
		this.origParent = null;
		this.origNext = null;
	}
	ngOnDestroy(): void {
		if (this.expanded) { this.renderer.removeClass(document.body, 'remit-focus-lock'); }
	}

	// Click a summary chip to show only those rows; click again to clear.
	setFilter(status: string): void {
		this.filterStatus = this.filterStatus === status ? null : status;
		this.persistScratch();
	}

	searchText = '';
	sortKey: string | null = null;
	sortDir = 1;
	private readonly ACTION = ['unmatched', 'over', 'multiple'];
	private readonly NUMERIC = ['line_no', 'paid_amount', 'matched_balance', 'alloc_amount'];

	get actionCount(): number {
		return (this.batch?.lines || []).filter((l: any) => this.ACTION.includes(l.match_status)).length;
	}
	get actionTotal(): number {
		return (this.batch?.lines || []).filter((l: any) => this.ACTION.includes(l.match_status))
			.reduce((a: number, l: any) => a + parseFloat(l.paid_amount || 0), 0);
	}

	setSort(key: string): void {
		if (this.sortKey === key) { this.sortDir = -this.sortDir; }
		else { this.sortKey = key; this.sortDir = 1; }
	}

	// Paid − invoice balance: negative = short (partial), positive = over. null when not applicable.
	lineGap(l: any): string | null {
		if (l.match_status !== 'partial' && l.match_status !== 'over') { return null; }
		if (l.matched_balance === null || l.matched_balance === '') { return null; }
		const g = Math.round((parseFloat(l.paid_amount) - parseFloat(l.matched_balance)) * 100) / 100;
		if (g < 0) { return '$' + Math.abs(g).toFixed(2) + ' short'; }
		if (g > 0) { return '$' + g.toFixed(2) + ' over'; }
		return null;
	}

	get filteredLines(): any[] {
		let out = this.batch?.lines || [];
		if (this.filterStatus === 'action') { out = out.filter((l: any) => this.ACTION.includes(l.match_status)); }
		else if (this.filterStatus) { out = out.filter((l: any) => l.match_status === this.filterStatus); }
		const q = this.searchText.trim().toLowerCase();
		if (q) {
			out = out.filter((l: any) =>
				(l.invoice_ref || '').toLowerCase().includes(q) ||
				(l.matched_transno || '').toString().toLowerCase().includes(q) ||
				(l.description || '').toLowerCase().includes(q) ||
				(l.match_note || '').toLowerCase().includes(q) ||
				(l.paid_amount || '').toString().includes(q) ||
				(l.alloc_amount || '').toString().includes(q));
		}
		if (this.sortKey) {
			const k = this.sortKey, dir = this.sortDir, num = this.NUMERIC.includes(k);
			out = out.slice().sort((a: any, b: any) => {
				const av = a[k], bv = b[k];
				const cmp = num ? (parseFloat(av || 0) - parseFloat(bv || 0))
					: (av || '').toString().localeCompare((bv || '').toString(), undefined, { numeric: true });
				return cmp * dir;
			});
		}
		return out;
	}

	// $ total behind each summary chip (null = all lines).
	statusTotal(status: string | null): number {
		const all = this.batch?.lines || [];
		const set = status ? all.filter((l: any) => l.match_status === status) : all;
		return set.reduce((a: number, l: any) => a + parseFloat(l.paid_amount || 0), 0);
	}

	dragging = false;

	onFile(event: any): void {
		this.acceptFile(event?.target?.files?.[0]);
	}

	onDrop(event: DragEvent): void {
		event.preventDefault();
		this.dragging = false;
		this.acceptFile(event.dataTransfer?.files?.[0]);
	}

	onDragOver(event: DragEvent): void {
		event.preventDefault();
		this.dragging = true;
	}

	onDragLeave(event: DragEvent): void {
		event.preventDefault();
		this.dragging = false;
	}

	private acceptFile(file: File | null | undefined): void {
		if (!file) { return; }
		const lower = file.name.toLowerCase();
		this.fileName = file.name;

		// Excel: convert the first sheet to CSV in-browser, then feed the fast CSV path (no AI).
		if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
			const reader = new FileReader();
			reader.onload = () => {
				try {
					const wb = XLSX.read(new Uint8Array(reader.result as ArrayBuffer), { type: 'array' });
					const ws = wb.Sheets[wb.SheetNames[0]];
					const csv = XLSX.utils.sheet_to_csv(ws);
					this.fileKind = 'csv';
					this.fileBase64 = 'data:text/csv;base64,' + btoa(unescape(encodeURIComponent(csv)));
				} catch (e) {
					this.clearFile();
					this.globalSearchService.showNotification('Could not read that Excel file.', 'danger', 'bottom', 'right');
				}
			};
			reader.readAsArrayBuffer(file);
			return;
		}

		const kind: 'pdf' | 'csv' | '' = lower.endsWith('.pdf') ? 'pdf'
			: (lower.endsWith('.csv') || lower.endsWith('.tsv') || lower.endsWith('.txt')) ? 'csv' : '';
		if (!kind) {
			this.fileName = '';
			this.globalSearchService.showNotification('Unsupported file — drop a PDF, Excel, or CSV.', 'warning', 'bottom', 'right');
			return;
		}
		this.fileKind = kind;
		const reader = new FileReader();
		reader.onload = () => { this.fileBase64 = String(reader.result || ''); };
		reader.readAsDataURL(file);
	}

	clearFile(): void {
		this.fileName = ''; this.fileBase64 = ''; this.fileKind = '';
	}

	canParse(): boolean {
		return !this.parsing && (this.pasteText.trim().length > 0 || (!!this.fileBase64 && !!this.fileKind));
	}

	// Honest progress copy: a PDF may need the AI read; CSV/clean paste is instant.
	get parsingLabel(): string {
		if (this.fileKind === 'pdf') { return 'Reading the PDF…'; }
		if (this.fileKind === 'csv') { return 'Parsing the file…'; }
		return 'Reading the remittance…';
	}
	get parsingSub(): string {
		return this.fileKind === 'csv'
			? 'Matching to open vendor invoices…'
			: 'Extracting rows, then matching to open vendor invoices…';
	}

	parse(): void {
		if (!this.canParse() || !this.vendor?.supplierid) { return; }
		const payload: any = { supplierno: this.vendor.supplierid };
		if (this.fileBase64 && this.fileKind === 'pdf') { payload.pdf = this.fileBase64; payload.source_name = this.fileName; }
		else if (this.fileBase64 && this.fileKind === 'csv') { payload.csv = this.fileBase64; payload.source_name = this.fileName; }
		else { payload.text = this.pasteText; }

		this.parsing = true;
		this.paymentsService.apRemitParse(payload).subscribe((res: any) => {
			this.parsing = false;
			if (res && res.success) {
				this.batch = res.batch;
				this.mode = 'review';
				this.appliedToPayment = false;
				// Open on the lines that need a decision; noise is one click away.
				this.filterStatus = this.actionCount > 0 ? 'action' : null;
				this.persistScratch();
			} else {
				this.globalSearchService.showNotification('Remittance parse failed: ' + (res?.message || 'unknown error'), 'danger', 'bottom', 'right');
			}
		}, () => {
			this.parsing = false;
			this.globalSearchService.showNotification('Remittance parse failed.', 'danger', 'bottom', 'right');
		});
	}

	// The batch lives only on the client (nothing is staged server-side) — rematch
	// sends the lines back and the server re-matches them against the ledger as it
	// is right now.
	private rematchPayload(): any {
		return {
			supplierno: this.vendor?.supplierid,
			stated_total: this.statedTotal,
			source_name: this.batch?.source_name || '',
			lines: (this.batch?.lines || []).map((l: any) => ({
				id: l.id,
				invoice_ref: l.invoice_ref,
				doc_type: l.doc_type,
				doc_date: l.doc_date,
				description: l.description,
				paid_amount: parseFloat(l.paid_amount || 0),
			})),
		};
	}

	// Re-match / re-parse without re-dropping the file. If the original source is still in
	// memory, re-run the full parse; otherwise re-match the held lines against the vendor's
	// current open invoices (e.g. after a missing invoice was entered).
	retry(): void {
		if (this.parsing) { return; }
		if ((this.fileBase64 && this.fileKind) || this.pasteText.trim().length > 0) {
			this.parse();
			return;
		}
		if (!this.batch) { return; }
		this.parsing = true;
		this.paymentsService.apRemitRematch(this.rematchPayload()).subscribe((res: any) => {
			this.parsing = false;
			if (res && res.success) {
				this.batch = res.batch;
				this.appliedToPayment = false;
				this.filterStatus = this.actionCount > 0 ? 'action' : null;
				this.persistScratch();
			} else {
				this.globalSearchService.showNotification('Re-match failed: ' + (res?.message || 'unknown error'), 'danger', 'bottom', 'right');
			}
		}, () => {
			this.parsing = false;
			this.globalSearchService.showNotification('Re-match failed.', 'danger', 'bottom', 'right');
		});
	}

	// Applyable: a line matched to an open invoice. Credits never apply here (they're
	// applied in the Allocate section); settled/unmatched lines have nothing to allocate.
	isSelectable(line: any): boolean {
		if (!line?.matched_supptransid) { return false; }
		return line.match_status === 'matched' || line.match_status === 'partial'
			|| line.match_status === 'over' || line.match_status === 'multiple';
	}

	isCredit(line: any): boolean {
		return line.match_status === 'credit';
	}

	toggleInclude(line: any): void {
		if (!this.isSelectable(line)) { return; }
		line.include = line.include ? 0 : 1;
		this.persistScratch();
	}

	updateAlloc(line: any, value: any): void {
		const v = parseFloat(value);
		line.alloc_amount = isNaN(v) ? '0.00' : v.toFixed(2);
		this.persistScratch();
	}

	// Sum that will be pushed onto the payment's allocations.
	get appliedTotal(): number {
		if (!this.batch?.lines) { return 0; }
		const t = this.batch.lines.reduce((acc: number, l: any) => {
			if (!l.include || this.isCredit(l)) { return acc; }
			return acc + parseFloat(l.alloc_amount || 0);
		}, 0);
		return Math.round(t * 100) / 100;
	}

	// Open credits the vendor netted from the draft — surfaced so the bank-line
	// variance is explainable.
	//
	// Reads paid_amount (what the vendor deducted on the draft), NOT alloc_amount:
	// the server leaves alloc_amount at 0.00 on every credit line, so this totalled
	// 0 and `deltaIsCredits` could never fire. A perfectly netted draft therefore
	// showed a bare six-figure "Difference" with no explanation — ATD's 160 credits
	// / $121,199.48 read as an import error instead of the arithmetic it is.
	get openCreditTotal(): number {
		if (!this.batch?.lines) { return 0; }
		const t = this.batch.lines.filter((l: any) => l.match_status === 'credit' && l.matched_supptransid)
			.reduce((a: number, l: any) => a + Math.abs(parseFloat(l.paid_amount || 0)), 0);
		return Math.round(t * 100) / 100;
	}

	// What those credit notes still have to give, per the ledger right now. Can be
	// less than openCreditTotal when a note was partly used already.
	get creditAvailableTotal(): number {
		if (!this.batch?.lines) { return 0; }
		const t = this.creditLines.reduce((a: number, l: any) =>
			a + Math.abs(parseFloat(l.credit_available || l.paid_amount || 0)), 0);
		return Math.round(t * 100) / 100;
	}

	get creditLines(): any[] {
		if (!this.batch?.lines) { return []; }
		return this.batch.lines.filter((l: any) => l.match_status === 'credit' && l.matched_supptransid);
	}

	get includedCount(): number {
		if (!this.batch?.lines) { return 0; }
		return this.batch.lines.filter((l: any) => l.include).length;
	}

	get statedTotal(): number {
		return this.batch ? parseFloat(this.batch.recon?.stated_total || 0) : 0;
	}

	// The bank withdrawal this payment is being posted against (bank rec hosting only).
	get bankAmount(): number | null {
		if (!this.bankRecContext) { return null; }
		const a = Math.abs(Number(this.bankRecContext.amount) || 0);
		return a > 0 ? Math.round(a * 100) / 100 : null;
	}

	// Difference between what will be applied and the money that actually moved
	// (bank line when hosted in bank rec, else the remittance's own total).
	get delta(): number {
		const target = this.bankAmount !== null ? this.bankAmount : this.statedTotal;
		return Math.round((this.appliedTotal - target) * 100) / 100;
	}

	// True when the gap is exactly the open credits — the draft was net of credits.
	get deltaIsCredits(): boolean {
		return this.delta !== 0 && Math.abs(Math.abs(this.delta) - this.openCreditTotal) <= 0.01;
	}

	get selectableLines(): any[] {
		if (!this.batch?.lines) { return []; }
		return this.batch.lines.filter((l: any) => this.isSelectable(l));
	}

	get allSelected(): boolean {
		const s = this.selectableLines;
		return s.length > 0 && s.every((l: any) => l.include);
	}

	toggleAll(): void {
		const target = this.allSelected ? 0 : 1;
		this.selectableLines.forEach((l: any) => (l.include = target));
		this.persistScratch();
	}

	statusLabel(status: string): string {
		return status === 'already_settled' ? 'settled' : status;
	}

	// A line is worth inspecting when it didn't cleanly match.
	hasDetail(status: string): boolean {
		return status === 'already_settled' || status === 'unmatched' || status === 'multiple' || status === 'over' || status === 'credit';
	}

	detailLine: any = null;
	detailData: any = null;
	detailLoading = false;

	openDetail(line: any): void {
		if (!this.hasDetail(line.match_status)) { return; }
		this.detailLine = line;
		this.detailData = null;
		this.detailLoading = true;
		this.paymentsService.apRemitLineDetail({
			supplierno: this.vendor?.supplierid,
			invoice_ref: line.invoice_ref,
			paid_amount: parseFloat(line.paid_amount || 0),
			matched_supptransid: line.matched_supptransid || 0,
		}).subscribe((res: any) => {
			this.detailLoading = false;
			this.detailData = res && res.success ? res : { error: (res?.message || 'Could not load details') };
		}, () => {
			this.detailLoading = false;
			this.detailData = { error: 'Could not load details' };
		});
	}

	closeDetail(): void {
		this.detailLine = null;
		this.detailData = null;
	}

	// ---- Validation exports (pre-apply) ----
	private exportName(ext: string): string {
		const d = new Date();
		const stamp = d.getFullYear() + ('0' + (d.getMonth() + 1)).slice(-2) + ('0' + d.getDate()).slice(-2);
		return 'ap-remittance-' + (this.vendor?.supplierid || '') + '-' + stamp + '.' + ext;
	}

	private exportRows(): any[][] {
		return (this.batch?.lines || []).map((l: any, i: number) => ([
			i + 1,
			l.invoice_ref,
			l.description || '',
			Number(l.paid_amount),
			l.matched_transno || '',
			l.matched_balance != null ? Number(l.matched_balance) : '',
			this.statusLabel(l.match_status),
			l.include ? 'Yes' : 'No',
			l.include ? Number(l.alloc_amount) : 0,
			l.match_note || '',
		]));
	}

	private summaryPairs(): string[][] {
		const r = this.batch?.recon || {};
		const c = r.counts || {};
		const pairs = [
			['Vendor', (this.vendor?.supplierid || '') + ' ' + (this.vendor?.suppname || '')],
			['Remittance total', '$' + Number(r.stated_total || 0).toFixed(2)],
			['Applying to payment', '$' + this.appliedTotal.toFixed(2)],
		];
		if (this.bankAmount !== null) { pairs.push(['Bank withdrawal', '$' + this.bankAmount.toFixed(2)]); }
		pairs.push(
			['Difference', '$' + this.delta.toFixed(2)],
			['Lines', (this.includedCount) + ' of ' + (r.line_count || 0) + ' selected'],
			['Matched / Partial / Over', (c.matched || 0) + ' / ' + (c.partial || 0) + ' / ' + (c.over || 0)],
			['Settled / Multiple / Unmatched / Credit', (c.already_settled || 0) + ' / ' + (c.multiple || 0) + ' / ' + (c.unmatched || 0) + ' / ' + (c.credit || 0)],
		);
		return pairs;
	}

	private readonly exportCols = ['#', 'Invoice Ref', 'Description', 'Paid', 'ERP Doc', 'Balance', 'Status', 'Apply?', 'Allocate', 'Note'];

	exportXlsx(): void {
		if (!this.batch) { return; }
		const r = this.batch.recon || {};
		const c = r.counts || {};
		const money = '$#,##0.00';
		const cols = this.exportCols;
		const NC = cols.length;
		const ACCENT = '183D78', HEAD = '183D78', WHITE = 'FFFFFF', BAND = 'EEF3FB', BORD = 'DADDE3', INK = '1F2430', MUT = '6B7280';
		const thin = { style: 'thin', color: { rgb: BORD } };
		const box = { top: thin, bottom: thin, left: thin, right: thin };

		const rows: any[][] = [];
		rows.push(['AP Remittance Validation']);
		rows.push([(this.vendor?.supplierid || '') + '   ' + (this.vendor?.suppname || '')]);
		rows.push([]);
		const sum0 = rows.length;
		rows.push(['Remittance total', this.statedTotal]);
		rows.push(['Applying to payment', this.appliedTotal]);
		rows.push(['Difference', this.delta]);
		rows.push(['Lines selected', this.includedCount + ' of ' + (r.line_count || 0)]);
		rows.push(['Status', 'matched ' + (c.matched || 0) + ' · partial ' + (c.partial || 0) + ' · over ' + (c.over || 0) + ' · settled ' + (c.already_settled || 0) + ' · multiple ' + (c.multiple || 0) + ' · unmatched ' + (c.unmatched || 0) + ' · credit ' + (c.credit || 0)]);
		const sum1 = rows.length - 1;
		rows.push([]);
		const hRow = rows.length;
		rows.push(cols.slice());
		const d0 = rows.length;
		let sumPaid = 0, sumAlloc = 0;
		(this.batch.lines || []).forEach((l: any, i: number) => {
			const paid = Number(l.paid_amount) || 0;
			const alloc = l.include ? (Number(l.alloc_amount) || 0) : 0;
			sumPaid += paid; sumAlloc += alloc;
			rows.push([i + 1, l.invoice_ref, l.description || '', paid, l.matched_transno || '',
				l.matched_balance != null && l.matched_balance !== '' ? Number(l.matched_balance) : '',
				this.statusLabel(l.match_status), l.include ? 'Yes' : 'No', alloc, l.match_note || '']);
		});
		const d1 = rows.length - 1;
		rows.push(['', '', 'TOTAL', Math.round(sumPaid * 100) / 100, '', '', '', '', Math.round(sumAlloc * 100) / 100, '']);
		const tRow = rows.length - 1;

		const ws: any = XLSX.utils.aoa_to_sheet(rows);
		ws['!cols'] = [{ wch: 5 }, { wch: 14 }, { wch: 38 }, { wch: 12 }, { wch: 11 }, { wch: 12 }, { wch: 12 }, { wch: 7 }, { wch: 12 }, { wch: 36 }];
		ws['!rows'] = []; ws['!rows'][0] = { hpt: 26 }; ws['!rows'][1] = { hpt: 16 };
		ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: NC - 1 } }, { s: { r: 1, c: 0 }, e: { r: 1, c: NC - 1 } }];
		ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: hRow, c: 0 }, e: { r: d1, c: NC - 1 } }) };

		const cell = (ri: number, ci: number): any => {
			const a = XLSX.utils.encode_cell({ r: ri, c: ci });
			if (!ws[a]) { ws[a] = { t: 's', v: '' }; }
			return ws[a];
		};
		const isMoneyCol = (ci: number) => ci === 3 || ci === 5 || ci === 8;

		cell(0, 0).s = { font: { bold: true, sz: 16, color: { rgb: WHITE } }, fill: { fgColor: { rgb: ACCENT } }, alignment: { vertical: 'center', horizontal: 'left', indent: 1 } };
		cell(1, 0).s = { font: { sz: 11, color: { rgb: MUT } }, alignment: { horizontal: 'left', indent: 1 } };

		for (let ri = sum0; ri <= sum1; ri++) {
			cell(ri, 0).s = { font: { bold: true, color: { rgb: INK } } };
			const v = cell(ri, 1);
			if (ri <= sum0 + 2) {
				const warn = (ri === sum0 + 2 && this.delta !== 0);
				v.t = 'n'; v.z = money;
				v.s = { font: { bold: ri === sum0 + 1, color: { rgb: warn ? 'C62828' : INK } }, numFmt: money };
			} else {
				v.s = { font: { color: { rgb: INK } } };
			}
		}

		for (let ci = 0; ci < NC; ci++) {
			cell(hRow, ci).s = { font: { bold: true, color: { rgb: WHITE } }, fill: { fgColor: { rgb: HEAD } },
				alignment: { vertical: 'center', horizontal: isMoneyCol(ci) ? 'right' : (ci === 0 || ci === 7 ? 'center' : 'left') }, border: box };
		}

		for (let ri = d0; ri <= d1; ri++) {
			const band = ((ri - d0) % 2) === 1;
			for (let ci = 0; ci < NC; ci++) {
				const cc = cell(ri, ci);
				const s: any = { border: box, alignment: { vertical: 'center' } };
				if (band) { s.fill = { fgColor: { rgb: BAND } }; }
				if (isMoneyCol(ci)) {
					if (cc.v !== '') { cc.t = 'n'; cc.z = money; s.numFmt = money; }
					s.alignment.horizontal = 'right';
				}
				if (ci === 0 || ci === 7) { s.alignment.horizontal = 'center'; }
				if (ci === 2 || ci === 9) { s.alignment.wrapText = false; }
				cc.s = s;
			}
		}

		for (let ci = 0; ci < NC; ci++) {
			const cc = cell(tRow, ci);
			const s: any = { font: { bold: true, color: { rgb: INK } }, fill: { fgColor: { rgb: 'DCE6F5' } },
				border: { top: { style: 'medium', color: { rgb: HEAD } }, bottom: thin, left: thin, right: thin }, alignment: { vertical: 'center' } };
			if (ci === 3 || ci === 8) { cc.t = 'n'; cc.z = money; s.numFmt = money; s.alignment.horizontal = 'right'; }
			if (ci === 2) { s.alignment.horizontal = 'right'; }
			cc.s = s;
		}

		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, 'AP Remittance');
		XLSX.writeFile(wb, this.exportName('xlsx'));
	}

	exportPdf(): void {
		if (!this.batch) { return; }
		const doc = new jsPDF({ orientation: 'landscape' });
		doc.setFontSize(14);
		doc.text('AP remittance validation', 14, 14);
		doc.setFontSize(9);
		let y = 22;
		this.summaryPairs().forEach(p => { doc.text(p[0] + ':  ' + p[1], 14, y); y += 5; });
		autoTable(doc, {
			startY: y + 2,
			head: [this.exportCols],
			body: this.exportRows().map(r => r.map((v, i) => (i === 3 || i === 5 || i === 8) && v !== '' ? Number(v).toFixed(2) : v)),
			styles: { fontSize: 7, cellPadding: 1.5 },
			headStyles: { fillColor: [24, 61, 120] },
			columnStyles: { 3: { halign: 'right' }, 5: { halign: 'right' }, 8: { halign: 'right' } },
		});
		doc.save(this.exportName('pdf'));
	}

	statusClass(status: string): string {
		switch (status) {
		case 'matched': return 'st-matched';
		case 'partial': return 'st-partial';
		case 'over': return 'st-over';
		case 'multiple': return 'st-multiple';
		case 'already_settled': return 'st-settled';
		case 'credit': return 'st-credit';
		default: return 'st-unmatched';
		}
	}

	applying = false;

	/**
	 * Push the included lines onto the host payment screen's allocations. Money
	 * never moves here — the operator still reviews and hits Post Payments.
	 *
	 * The batch is re-verified server-side FIRST (fresh rematch against the
	 * ledger as it is right now), because the host may not have any invoice list
	 * loaded to cap against — a vendor with thousands of open invoices is
	 * exactly who this feature is for. The operator's include/alloc edits are
	 * kept; a line whose invoice was settled or held since the parse is dropped
	 * and flagged instead of being blindly allocated (you never allocate to a
	 * settled invoice).
	 */
	applyToPayment(): void {
		if (!this.batch || this.applying) { return; }
		this.creditPlan = null;
		if (!this.batch.lines?.some((l: any) => l.include && !this.isCredit(l))) {
			this.globalSearchService.showNotification('Nothing selected to apply — include at least one matched line.', 'warning', 'bottom', 'right');
			return;
		}
		this.applying = true;
		this.paymentsService.apRemitRematch(this.rematchPayload()).subscribe((res: any) => {
			this.applying = false;
			if (!(res && res.success && res.batch)) {
				this.globalSearchService.showNotification('Could not verify the batch against current invoices: ' + (res?.message || 'unknown error'), 'danger', 'bottom', 'right');
				return;
			}
			const fresh: { [id: number]: any } = {};
			for (const f of (res.batch.lines || [])) { fresh[f.id] = f; }

			const APPLYABLE = ['matched', 'partial', 'over', 'multiple'];
			const lines: any[] = [];
			let dropped = 0;
			for (const l of (this.batch.lines || [])) {
				if (!l.include || this.isCredit(l)) { continue; }
				const f = fresh[l.id];
				if (!f || !f.matched_supptransid || !APPLYABLE.includes(f.match_status)) {
					// State changed under us — reflect it on the row and leave it out.
					dropped++;
					l.include = 0;
					if (f) { l.match_status = f.match_status; l.match_note = f.match_note; l.matched_balance = f.matched_balance; l.alloc_amount = f.alloc_amount; }
					continue;
				}
				const cap = Math.abs(parseFloat(f.matched_balance || 0));
				const alloc = Math.round(Math.min(parseFloat(l.alloc_amount || 0), cap) * 100) / 100;
				if (alloc <= 0) { dropped++; l.include = 0; continue; }
				lines.push({ id: l.id, supptransid: f.matched_supptransid, invoice_ref: l.invoice_ref, alloc });
			}
			if (dropped) {
				this.globalSearchService.showNotification(dropped + ' line(s) dropped — settled or changed since the parse. Review the flagged rows.', 'warning', 'bottom', 'right');
			}
			if (!lines.length) {
				this.persistScratch();
				return;
			}

			const credits = this.applyCredits ? this.creditLines : [];
			if (!credits.length) {
				this.creditPlan = null;
				this.emitApply(lines, []);
				return;
			}

			// Netted draft: pair the credits to these invoices server-side (live
			// balances, capped both ways), then hand the payment only the cash slice
			// each invoice still needs. Invoices $315,265.12 − credits $121,199.48 =
			// the $194,065.64 that actually left the bank.
			this.applying = true;
			this.paymentsService.apRemitCreditPlan({
				supplierno: this.vendor?.supplierid,
				invoices: lines.map((l: any) => ({ supptransid: l.supptransid, alloc: l.alloc })),
				credits: credits.map((c: any) => ({ supptransid: c.matched_supptransid })),
			}).subscribe((cp: any) => {
				this.applying = false;
				if (!(cp && cp.success)) {
					this.globalSearchService.showNotification(
						'Could not plan the credit application: ' + (cp?.message || 'unknown error') + ' — nothing applied.',
						'danger', 'bottom', 'right');
					return;
				}
				this.creditPlan = cp;
				const cash: { [id: number]: number } = {};
				for (const p of (cp.invoice_plan || [])) { cash[+p.supptransid] = parseFloat(p.cash_alloc || 0); }
				for (const l of lines) {
					if (cash[+l.supptransid] !== undefined) { l.alloc = cash[+l.supptransid]; }
				}
				if ((cp.credits_unplaced || []).length) {
					this.globalSearchService.showNotification(
						(cp.credits_unplaced.length) + ' credit(s) could not be placed — more credit than invoice on this draft. They stay open on the vendor.',
						'warning', 'bottom', 'right');
				}
				this.emitApply(lines.filter((l: any) => l.alloc > 0), cp.credit_allocations || []);
			}, () => {
				this.applying = false;
				this.globalSearchService.showNotification('Could not plan the credit application — nothing applied.', 'danger', 'bottom', 'right');
			});
		}, () => {
			this.applying = false;
			this.globalSearchService.showNotification('Could not verify the batch against current invoices.', 'danger', 'bottom', 'right');
		});
	}

	// One exit from apply, whether or not credits are in play. credit_allocations
	// ride along to the host, which passes them straight to the existing post path
	// (add()); this component still writes nothing itself.
	private emitApply(lines: any[], creditAllocations: any[]): void {
		this.appliedToPayment = true;
		this.persistScratch();
		this.apply.emit({
			lines,
			credit_allocations: creditAllocations,
			credit_applied_total: parseFloat(this.creditPlan?.credit_applied_total || 0),
			stated_total: this.statedTotal,
			applied_total: Math.round(lines.reduce((a: number, l: any) => a + l.alloc, 0) * 100) / 100,
		});
	}

	// Post from right here: allocations are already applied; the host validates
	// its payment form, stages the payment, and runs the one existing post path.
	requestPost(): void {
		if (!this.appliedToPayment) { return; }
		this.postNow.emit();
	}

	// Called by the host screen once the payment the batch fed was actually posted.
	// The payment + suppallocs + the bank-rec match link ARE the record; the batch
	// was never stored server-side, so there is nothing to latch — just conclude.
	onPaymentPosted(paymentRef: string): void {
		if (!this.batch || !this.appliedToPayment) { return; }
		this.appliedResult = { payment_ref: paymentRef || '' };
		this.mode = 'done';
		if (this.expanded) { this.toggleExpand(); }   // leave focus mode so the done state sits back in the screen
		this.clearScratch();
	}

	reset(): void {
		const hadCredits = !!this.creditPlan;
		this.mode = 'input';
		this.pasteText = '';
		this.clearFile();
		this.batch = null;
		this.appliedToPayment = false;
		this.appliedResult = null;
		this.applyCredits = true;
		this.creditPlan = null;
		this.filterStatus = null;
		this.searchText = '';
		this.clearScratch();   // Cancel discards the working copy
		// Cancelling after an apply must also drop the credit pairs the host is
		// holding — otherwise they'd ride along on the next, unrelated payment.
		if (hadCredits) { this.apply.emit({ lines: [], credit_allocations: [], reset: true }); }
	}
}
